import { useCallback, useEffect, useRef, useState } from 'react';
import { msalInstance, setMsalReturnTo } from '../msalConfig';

/* Acquiring a delegated Microsoft Graph token from the browser.
 *
 * Extracted from YourDay once a second page needed it. Every branch here exists
 * because of a real failure, so it is worth reading before changing:
 *
 *  - MSAL caches in sessionStorage while the hub session lives in localStorage,
 *    so a restored tab routinely has a valid hub session and NO MSAL account.
 *    The interactive path is therefore common, not exceptional.
 *  - It cannot be an automatic retry: the interactive call must originate from a
 *    user gesture or the browser blocks it. A silent miss surfaces a button.
 *  - The request is pinned to the hub identity, which is what stops Microsoft
 *    showing a "Pick an account" chooser. Several staff hold two work identities
 *    and picking the wrong one gets a server-side 403 that reads like a bug.
 *  - Popup on desktop, redirect on mobile: iOS Safari blocks or mangles popups,
 *    and it is where this path fires most.
 *  - `interaction_in_progress` leaves a flag only `handleRedirectPromise` clears
 *    (at app boot), so the honest remedy is a page reload, offered as a button.
 *  - `block_nested_popups` means this window IS a leftover sign-in popup. A popup
 *    cannot open a popup but it can redirect itself, so that is the escape hatch.
 */

function hubEmail() {
  return window.localStorage.getItem('e') || '';
}

/** The MSAL account matching the signed-in hub user, or null.
 *
 * Deliberately returns null rather than guessing when there's no email match:
 * acquiring a token for the wrong identity produces a confusing 403 later.
 */
export function resolveAccount() {
  const email = hubEmail().toLowerCase();
  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) return null;
  const match = accounts.find((a) => (a.username || '').toLowerCase() === email);
  if (match) return match;
  return !email && accounts.length === 1 ? accounts[0] : null;
}

/**
 * @param {{scopes: string[]}} request  the scope set for this feature
 * @param {string} returnPath           where to come back to after a redirect
 * @param {(token: string, opts: {preferCache: boolean}) => void} onToken
 */
export default function useGraphToken(request, returnPath, onToken) {
  // 'loading' → resolving silently; 'connect' → needs a user gesture;
  // 'ready' → a token was handed to onToken.
  const [phase, setPhase] = useState('loading');
  const [authError, setAuthError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const started = useRef(false);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const pinned = useCallback(() => {
    const account = resolveAccount();
    if (account) return { ...request, account };
    const email = hubEmail();
    return email ? { ...request, loginHint: email } : { ...request };
  }, [request]);

  const acquire = useCallback(async ({ preferCache = true } = {}) => {
    const account = resolveAccount();
    if (!account) { setPhase('connect'); return; }
    try {
      const result = await msalInstance.acquireTokenSilent({ ...request, account });
      setAuthError('');
      setPhase('ready');
      onTokenRef.current(result.accessToken, { preferCache });
    } catch (e) {
      // The consent-needed codes are expected and get no banner; anything else is
      // surfaced so a misconfiguration is diagnosable rather than looking like an
      // endless consent loop.
      const expected = ['interaction_required', 'login_required', 'consent_required', 'no_account_error'];
      if (e?.errorCode && !expected.includes(e.errorCode)) {
        setAuthError(e.errorMessage || e.message || '');
      }
      setPhase('connect');
    }
  }, [request]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    acquire({ preferCache: true });
  }, [acquire]);

  const connect = useCallback(async (isMobile) => {
    // A second click while Microsoft's window is open is exactly what produces
    // interaction_in_progress, after which every retry fails identically.
    if (connecting) return;
    setConnecting(true);
    setAuthError('');

    const req = pinned();
    const goRedirect = () => {
      setMsalReturnTo(returnPath);
      return msalInstance.acquireTokenRedirect(req);
    };

    try {
      if (isMobile) { await goRedirect(); return; }
      const result = await msalInstance.acquireTokenPopup(req);
      setPhase('ready');
      onTokenRef.current(result.accessToken, { preferCache: false });
    } catch (e) {
      const code = e?.errorCode || '';
      if (code === 'user_cancelled') {
        // They closed the window. Not an error; no banner.
      } else if (code === 'interaction_in_progress') {
        setNeedsReload(true);
        setAuthError('A Microsoft sign-in window was already open. Reload the page and try once more.');
      } else if (code === 'popup_window_error' || code === 'empty_window_error' || code === 'block_nested_popups') {
        try { await goRedirect(); return; } catch (e2) {
          setAuthError(e2?.errorMessage || e2?.message || 'Could not reach Microsoft.');
        }
      } else {
        setAuthError(e?.errorMessage || e?.message || 'Outlook was not connected.');
      }
    } finally {
      setConnecting(false);
    }
  }, [connecting, pinned, returnPath]);

  return { phase, authError, connecting, needsReload, connect, acquire, hubEmail: hubEmail() };
}
